import { useEffect, useState } from "react";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShieldWarning, Sparkle, TrendUp, Warning } from "@phosphor-icons/react";

const TIER_COLOR = {
  high:   "bg-red-100 text-red-800 border-red-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low:    "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function FraudWatch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // {claim, fraud, busy}
  const [benford, setBenford] = useState(null); // {employee, data}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/expenses/fraud-watch/overview");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const recheckVision = async (claim) => {
    setOpen((o) => ({ ...o, busy: true }));
    try {
      const r = await api.post(`/expenses/${claim.id}/recheck-fraud`, { run_vision: true });
      setOpen({ claim, fraud: r.data });
      toast.success("AI vision re-check complete");
      load();
    } catch (e) { toast.error("Re-check failed"); setOpen((o) => ({ ...o, busy: false })); }
  };

  const loadBenford = async (emp) => {
    try {
      const r = await api.get(`/expenses/fraud-watch/benford/${emp.employee_id}`);
      setBenford({ employee: emp, data: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not enough history for Benford analysis");
    }
  };

  const totals = data?.totals || {};

  return (
    <AppShell title="Fraud Watch">
      <div className="mb-5 flex items-start gap-3">
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900 flex-1 flex items-start gap-2">
          <ShieldWarning size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">AI-powered expense fraud surveillance — powered by DeepSeek.</div>
            <div className="text-xs mt-1">Every submitted claim is screened across 12 fraud patterns. Approvers see a risk pill on each card; this page surfaces the most concerning ones for deep review.</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="High risk" value={totals.high || 0} testid="fw-stat-high"/>
        <StatCard label="Medium risk" value={totals.medium || 0} testid="fw-stat-medium"/>
        <StatCard label="Clean" value={totals.low || 0} testid="fw-stat-low"/>
        <StatCard label="Approvers flagged" value={(data?.approver_stats || []).length} testid="fw-stat-approvers"/>
      </div>

      <SectionCard title="Top flagged claims" subtitle="Sorted by risk score. Click a row to see signals.">
        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
        {!loading && (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Risk</TableHead><TableHead>Title</TableHead><TableHead>Employee</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
              <TableHead>Signals</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data?.top_flagged_claims || []).length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-6 text-center text-zinc-500">No flagged claims yet. Either the model needs more history or your team is squeaky-clean.</TableCell></TableRow>
              )}
              {(data?.top_flagged_claims || []).map((c) => {
                const f = c.fraud || {};
                return (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => setOpen({ claim: c, fraud: f })}>
                    <TableCell><Badge className={TIER_COLOR[f.tier || "low"]}>{(f.tier || "low").toUpperCase()} · {f.risk_score || 0}</Badge></TableCell>
                    <TableCell><div className="font-medium">{c.title}</div></TableCell>
                    <TableCell>{c.employee_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(c.total_amount)}</TableCell>
                    <TableCell><Badge variant="outline">{c.status}</Badge></TableCell>
                    <TableCell className="text-xs">{(f.signals || []).length}</TableCell>
                    <TableCell><Button size="sm" variant="outline" data-testid={`fw-view-${c.id}`} onClick={(ev) => { ev.stopPropagation(); setOpen({ claim: c, fraud: f }); }}>Review</Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <SectionCard title="Repeat-offender employees" subtitle="Most flagged claims in this company.">
          {(data?.top_flagged_employees || []).length === 0 && <div className="text-sm text-zinc-500">No repeat patterns yet.</div>}
          {(data?.top_flagged_employees || []).map((e) => (
            <div key={e.employee_id} className="flex items-center justify-between border-b last:border-0 py-2.5">
              <div>
                <div className="text-sm font-medium">{e.employee_name || e.employee_id}</div>
                <div className="text-[11px] text-zinc-500">{e.claim_count} claims · {e.high_risk} high · {e.medium_risk} medium</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">max {e.max_score}</Badge>
                <Button size="sm" variant="outline" data-testid={`fw-benford-${e.employee_id}`} onClick={() => loadBenford(e)} className="gap-1"><TrendUp size={12}/> Benford</Button>
              </div>
            </div>
          ))}
        </SectionCard>

        <SectionCard title="Approver behaviour" subtitle="Managers who almost never reject. Worth a closer look.">
          {(data?.approver_stats || []).length === 0 && <div className="text-sm text-zinc-500">No anomalous approvers in this period.</div>}
          {(data?.approver_stats || []).map((a) => (
            <div key={a.approver_user_id} className="flex items-center justify-between border-b last:border-0 py-2.5">
              <div>
                <div className="text-sm font-medium flex items-center gap-2">{a.approver_name}
                  {a.flags.includes("never_rejects") && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]"><Warning size={10} className="mr-0.5"/> never rejects</Badge>}
                </div>
                <div className="text-[11px] text-zinc-500">{a.approvals}/{a.total} approved ({Math.round(a.approve_rate * 100)}%) · {a.rejections} rejected</div>
              </div>
            </div>
          ))}
        </SectionCard>
      </div>

      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="fw-signal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Fraud signals
              {open?.fraud && <Badge className={TIER_COLOR[open.fraud.tier]}>{open.fraud.tier?.toUpperCase()} · {open.fraud.risk_score}/100</Badge>}
            </DialogTitle>
            <DialogDescription>
              {open?.claim?.title} · {inr(open?.claim?.total_amount)} · {open?.claim?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-auto">
            {(open?.fraud?.signals || []).length === 0 && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">No fraud signals detected. ✓</div>
            )}
            {(open?.fraud?.signals || []).map((s, idx) => (
              <div key={idx} className={`border rounded p-3 ${s.severity === 3 ? "bg-red-50 border-red-200" : s.severity === 2 ? "bg-amber-50 border-amber-200" : "bg-zinc-50 border-zinc-200"}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-xs font-semibold uppercase tracking-wide">{s.code}</div>
                  <div className="text-[10px] text-zinc-500">severity {s.severity}/3</div>
                </div>
                <div className="text-sm">{s.label}</div>
                {s.detail && Object.keys(s.detail).length > 0 && (
                  <pre className="mt-2 text-[11px] text-zinc-700 bg-white/60 border rounded p-2 overflow-auto">{JSON.stringify(s.detail, null, 2)}</pre>
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" disabled={open?.busy} onClick={() => recheckVision(open.claim)} data-testid="fw-recheck-vision-btn">
              <Sparkle size={12} weight="fill" className="mr-1 text-amber-500"/>
              {open?.busy ? "Running AI vision…" : "Re-check with DeepSeek vision"}
            </Button>
            <Button onClick={() => setOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!benford} onOpenChange={() => setBenford(null)}>
        <DialogContent data-testid="fw-benford-dialog">
          <DialogHeader>
            <DialogTitle>Benford's Law check — {benford?.employee?.employee_name}</DialogTitle>
            <DialogDescription>
              Real-world expense amounts follow Benford's Law (more 1s than 9s as the first digit). Fabricated numbers deviate.
            </DialogDescription>
          </DialogHeader>
          {benford?.data && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className={benford.data.suspicious ? "bg-red-100 text-red-800 border-red-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}>
                  {benford.data.suspicious ? "Suspicious deviation" : "Within tolerance"}
                </Badge>
                <span className="text-xs text-zinc-500">n={benford.data.n} claims · χ²={benford.data.chi2}</span>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>First digit</TableHead><TableHead className="text-right">Observed</TableHead><TableHead className="text-right">Expected %</TableHead></TableRow></TableHeader>
                <TableBody>
                  {[1,2,3,4,5,6,7,8,9].map((d) => (
                    <TableRow key={d}>
                      <TableCell className="font-mono">{d}</TableCell>
                      <TableCell className="text-right tabular-nums">{benford.data.first_digit_counts[String(d)]}</TableCell>
                      <TableCell className="text-right text-zinc-500 text-xs">{benford.data.expected_pct[String(d)]}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter><Button onClick={() => setBenford(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
